import { useEffect } from "react";
import { Link } from "wouter";
import { scrollToTop } from "@/lib/utils";
import { Button } from "@/components/ui/button";

export default function AboutPage() {
  useEffect(() => {
    scrollToTop();
  }, []);
  
  return (
    <div className="pt-24 pb-16">
      <div className="container mx-auto px-4">
        <div className="text-center mb-12">
          <h1 className="font-serif text-4xl md:text-5xl font-bold text-[#1a365d] mb-4">About Sarah Willardsen</h1>
          <div className="w-24 h-1 bg-[#7d9b76] mx-auto mb-6"></div>
          <p className="max-w-3xl mx-auto text-lg">
            Meet the expert behind SW Accounting and learn about her professional journey spanning over two decades.
          </p>
        </div>
        
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12 mb-16">
          <div className="lg:col-span-1">
            <img 
              src="/images/team/sarah-adam-4.jpg" 
              alt="Sarah and Adam Willardsen" 
              className="rounded-lg shadow-lg w-full h-auto object-cover"
            />
            
            <div className="bg-[#f8f9fa] p-6 rounded-lg mt-6 shadow">
              <h3 className="font-serif text-xl font-bold text-[#1a365d] mb-4">Credentials</h3>
              <ul className="space-y-3">
                <li className="flex items-start">
                  <div className="text-[#7d9b76] mr-3 mt-1"><i className="fas fa-graduation-cap"></i></div>
                  <div>
                    <h4 className="font-bold">Bachelor of Science</h4>
                    <p>Accounting</p>
                  </div>
                </li>
                <li className="flex items-start">
                  <div className="text-[#7d9b76] mr-3 mt-1"><i className="fas fa-award"></i></div>
                  <div>
                    <h4 className="font-bold">Certified QuickBooks Pro Advisor</h4>
                    <p>Expert level certification</p>
                  </div>
                </li>
                <li className="flex items-start">
                  <div className="text-[#7d9b76] mr-3 mt-1"><i className="fas fa-certificate"></i></div>
                  <div>
                    <h4 className="font-bold">Sage-Intacct Certification</h4>
                    <p>Cloud financial management software</p>
                  </div>
                </li>
                <li className="flex items-start">
                  <div className="text-[#7d9b76] mr-3 mt-1"><i className="fas fa-medal"></i></div>
                  <div>
                    <h4 className="font-bold">Financial Officer Certification</h4>
                    <p>Advanced financial management</p>
                  </div>
                </li>
              </ul>
            </div>
          </div>
          
          <div className="lg:col-span-2">
            <h2 className="font-serif text-3xl font-bold text-[#1a365d] mb-6">Professional Background</h2>
            
            <p className="mb-4 text-lg">
              Sarah Willardsen brings over 20 years of accounting expertise to every client relationship. Her journey in the world of accounting began with a passion for numbers and a drive to help businesses succeed through proper financial management.
            </p>
            
            <p className="mb-4">
              After earning her Bachelor's degree in Accounting, Sarah worked with several prestigious accounting firms, gaining invaluable experience across various industries. This diverse background allows her to understand the unique financial challenges faced by different types of businesses, from small startups to established enterprises.
            </p>
            
            <p className="mb-6">
              In 2008, Sarah founded SW Accounting with a mission to provide personalized accounting services that go beyond basic number-crunching. She believes in building lasting partnerships with clients, becoming a trusted financial advisor who helps navigate complex financial decisions with confidence.
            </p>
            
            <h3 className="font-serif text-2xl font-bold text-[#1a365d] mb-4">Areas of Expertise</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
              <div className="bg-white p-4 rounded-lg border border-[#e9ecef]">
                <div className="text-[#7d9b76] text-2xl mb-2"><i className="fas fa-calculator"></i></div>
                <h4 className="font-bold text-lg mb-2">Financial Analysis</h4>
                <p>Deep insights into financial data to guide business decisions and strategy.</p>
              </div>
              
              <div className="bg-white p-4 rounded-lg border border-[#e9ecef]">
                <div className="text-[#7d9b76] text-2xl mb-2"><i className="fas fa-laptop-code"></i></div>
                <h4 className="font-bold text-lg mb-2">QuickBooks Optimization</h4>
                <p>Expert setup and management of QuickBooks for maximum efficiency.</p>
              </div>
              
              <div className="bg-white p-4 rounded-lg border border-[#e9ecef]">
                <div className="text-[#7d9b76] text-2xl mb-2"><i className="fas fa-chart-line"></i></div>
                <h4 className="font-bold text-lg mb-2">Strategic Planning</h4>
                <p>Forward-thinking financial strategies to support business growth.</p>
              </div>
              
              <div className="bg-white p-4 rounded-lg border border-[#e9ecef]">
                <div className="text-[#7d9b76] text-2xl mb-2"><i className="fas fa-file-invoice-dollar"></i></div>
                <h4 className="font-bold text-lg mb-2">Tax Planning</h4>
                <p>Proactive tax strategies to minimize liabilities and ensure compliance.</p>
              </div>
            </div>
            
            <h3 className="font-serif text-2xl font-bold text-[#1a365d] mb-4">Professional Philosophy</h3>
            
            <p className="mb-6">
              Sarah approaches accounting with a unique blend of technical precision and personalized care. She believes that behind every number is a business decision, and behind every business decision are people with goals and aspirations. This human-centered approach to accounting has earned her the trust and loyalty of clients across diverse industries.
            </p>
            
            <p className="mb-8">
              "My goal is to provide more than just accurate books," says Sarah. "I want to give my clients the financial clarity and confidence they need to make informed decisions that drive growth and success."
            </p>
            
            <div className="flex flex-wrap gap-4">
              <Link href="/booking">
                <Button className="bg-[#7d9b76] hover:bg-opacity-90">
                  Schedule a Consultation
                </Button>
              </Link>
              
              <Link href="/testimonials">
                <Button variant="outline" className="border-[#7d9b76] text-[#7d9b76] hover:bg-[#7d9b76] hover:text-white">
                  Read Client Testimonials
                </Button>
              </Link>
            </div>
          </div>
        </div>
        
        <div className="mb-16">
          <h2 className="font-serif text-3xl font-bold text-[#1a365d] mb-6 text-center">Meet Our Team</h2>
          <div className="w-24 h-1 bg-[#7d9b76] mx-auto mb-8"></div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="bg-white rounded-lg overflow-hidden shadow-lg">
              <img 
                src="/images/team/sarah-adam-2.jpg" 
                alt="Sarah and Adam Willardsen" 
                className="w-full h-auto"
              />
              <div className="p-6">
                <h3 className="font-serif text-xl font-bold text-[#1a365d] mb-2">Sarah & Adam Willardsen</h3>
                <p className="text-gray-700">
                  Sarah and her husband Adam bring complementary skills to SW Accounting. 
                  While Sarah focuses on accounting expertise, Adam brings his background in 
                  business management to help support operations and client relationships.
                </p>
              </div>
            </div>
            
            <div className="bg-white rounded-lg overflow-hidden shadow-lg">
              <img 
                src="/images/team/team-photo.jpeg" 
                alt="SW Accounting Team" 
                className="w-full h-auto"
              />
              <div className="p-6">
                <h3 className="font-serif text-xl font-bold text-[#1a365d] mb-2">Family-Owned Business</h3>
                <p className="text-gray-700">
                  As a family-owned business, we understand the unique challenges that small and 
                  medium-sized businesses face. We bring that personal touch to all our client 
                  relationships, treating your business with the same care we would our own.
                </p>
              </div>
            </div>
          </div>
        </div>
        
        <div className="bg-[#1a365d] text-white p-8 rounded-lg">
          <div className="flex flex-col md:flex-row items-center">
            <div className="md:w-2/3 mb-6 md:mb-0 md:pr-8">
              <h3 className="font-serif text-2xl font-bold mb-4">Ready to Transform Your Financial Management?</h3>
              <p className="mb-0">
                Book a consultation with Sarah today to discuss how SW Accounting can help your business achieve financial clarity and success.
              </p>
            </div>
            <div className="md:w-1/3 text-center">
              <Link href="/booking">
                <Button size="lg" className="bg-white text-[#1a365d] hover:bg-opacity-90">
                  Book Your Session Now
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
